<script setup>
import { useSelectedFamilyMemberState } from '@/core/composables/useSelectedFamilyMemberState'
import { useProfilesQuery } from '@/account/composables/useProfilesQuery'

const props = defineProps(['label'])

const { currentCustomerId, hasFamily } = useSessionData()

const { t } = useI18n()

const selectedFamilyMember = useSelectedFamilyMemberState()
const { data: profilesData, isPending: profilesIsLoading } = useProfilesQuery()

const profileItems = computed(() => {
  if (!hasFamily || profilesIsLoading.value !== false)
    return []
  return profilesData.value
    .filter(profile => profile.participating === true)
    .map(profile => ({
      ...profile,
      manager: profile.family_role === 'manager',
      label: `${profile.first_name} ${profile.last_name}`,
      value: profile.id,
    }))
    .sort((profileA, profileB) => {
      const pendingComparison
        = profileA.pending === profileB.pending ? 0 : profileA.pending ? -1 : 1
      const managerComparison
        = profileA.manager === profileB.manager ? 0 : profileA.manager ? -1 : 1
      const lastNameComparison = (profileA.last_name || '').localeCompare(
        profileB.last_name,
      )
      const firstNameComparison = (profileA.first_name || '').localeCompare(
        profileB.first_name,
      )
      return (
        pendingComparison * 8
        + managerComparison * 4
        + lastNameComparison * 2
        + firstNameComparison
      )
    })
})

watch(
  profileItems,
  () => {
    if (!selectedFamilyMember.value && profileItems.value.length > 0)
      selectedFamilyMember.value = profileItems.value[0]?.id
  },
  { immediate: true },
)
</script>

<template>
  <div
    v-if="hasFamily && profileItems.length > 1"
    class="family-dropdown"
  >
    <label
      for="family-dropdown"
      class="family-input-label mb-1 text-text-600 font-600"
    >
      {{ label ? label : t('Select Family Member') }}
    </label>
    <DSListboxInput
      v-model="selectedFamilyMember"
      :options="profileItems"
    />
  </div>
</template>

<i18n>
{
  "en-us": {
    "Select Family Member": "Select Family Member"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "Select Family Member": "Familienmitglied auswählen"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "Select Family Member": "Sélectionnez le membre de la famille"
}
</i18n>

<i18n locale="es" lang="json">
{
  "Select Family Member": "Seleccionar miembro de la familia"
}
</i18n>
